(() => {
  'use strict';
  if (customElements.get('pp-glass-video')) return;
  class GlassVideo extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.playButton = this.querySelector('[data-pgv-play]');
      this.muteButton = this.querySelector('[data-pgv-mute]');
      this.error = this.querySelector('[data-pgv-error]');
      if (!this.playButton || !this.muteButton) return;
      this.video = this.querySelector('video');
      this.muted = this.video?.muted || false;
      this.playButton.disabled = false;
      this.muteButton.disabled = false;
      this.playButton.addEventListener('click', () => this.togglePlay(), options);
      this.muteButton.addEventListener('click', () => {
        this.muted = !this.muted;
        if (this.video) this.video.muted = this.muted;
        this.sync();
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.video?.pause();
      }, options);
      if (this.video) this.bindVideo();
      this.sync();
    }
    disconnectedCallback() {
      this.video?.pause();
      this.listeners?.abort();
    }
    bindVideo() {
      const options = { signal: this.listeners.signal };
      this.video.controls = false;
      this.video.removeAttribute('controls');
      this.video.playsInline = true;
      this.video.addEventListener('play', () => {
        document.querySelectorAll('pp-glass-video').forEach(player => {
          if (player !== this) player.video?.pause();
        });
        this.sync();
      }, options);
      for (const event of ['pause', 'ended', 'volumechange', 'playing', 'canplay']) {
        this.video.addEventListener(event, () => {
          if (event === 'playing' || event === 'canplay' || event === 'pause') this.removeAttribute('data-loading');
          this.sync();
        }, options);
      }
      this.video.addEventListener('waiting', () => this.setAttribute('data-loading', ''), options);
      this.video.addEventListener('error', () => this.showError(), options);
    }
    async togglePlay() {
      this.error.hidden = true;
      if (!this.video) {
        const source = this.querySelector('template')?.content.querySelector('video');
        if (!source) { this.showError(); return; }
        this.video = source.cloneNode(true);
        this.video.muted = this.muted;
        this.prepend(this.video);
        this.bindVideo();
      }
      if (!this.video.paused) {
        this.video.pause();
        return;
      }
      if (this.video.error) this.video.load();
      if (this.video.ended) this.video.currentTime = 0;
      this.setAttribute('data-loading', '');
      try {
        await this.video.play();
      } catch (error) {
        if (error.name !== 'AbortError') this.showError();
      } finally {
        this.removeAttribute('data-loading');
        this.sync();
      }
    }
    showError() {
      this.removeAttribute('data-loading');
      this.error.textContent = this.dataset.errorLabel;
      this.error.hidden = false;
      this.sync();
    }
    sync() {
      const playing = !!this.video && !this.video.paused && !this.video.ended;
      this.muted = this.video ? this.video.muted : this.muted;
      this.toggleAttribute('data-playing', playing);
      this.querySelector('[data-pgv-play-icon]').hidden = playing;
      this.querySelector('[data-pgv-pause-icon]').hidden = !playing;
      this.querySelector('[data-pgv-volume-icon]').hidden = this.muted;
      this.querySelector('[data-pgv-muted-icon]').hidden = !this.muted;
      const playLabel = playing ? this.dataset.pauseLabel : this.dataset.playLabel;
      const soundLabel = this.muted ? this.dataset.unmuteLabel : this.dataset.muteLabel;
      this.playButton.setAttribute('aria-label', playLabel);
      this.playButton.title = playLabel;
      this.muteButton.setAttribute('aria-label', soundLabel);
      this.muteButton.title = soundLabel;
      this.muteButton.setAttribute('aria-pressed', String(this.muted));
    }
  }
  customElements.define('pp-glass-video', GlassVideo);
})();
